/**
 * Property error data transformation: parses property API error responses
 * into error table rows and builds property form data for API submission.
 */

import { convertBooleanValue, convertYesOrNoValue, extractValue } from '@/util/formatting';
import { getEntityFallbackValue } from '@/services/entity-tree';
import type { ErrorCell } from '../utils/fixErrorFieldMaps';
import { dashToNull } from '../utils/fixErrorFieldMaps';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

// ---- Property error parsing -----------------------------------------------

export const parsePropertyErrors = (
  tableData: Record<string, unknown>[]
): { rows: ErrorTableRow[]; failedCount: number } => {
  const rows: ErrorTableRow[] = [];
  let failedCount = 0;

  tableData.forEach((item: Record<string, unknown>, index: number) => {
    const makeCell = (
      field: string,
      opts?: { transform?: (v: unknown) => string | null }
    ): ErrorCell => {
      const raw = item[field] as string | null;
      const value = opts?.transform ? opts.transform(raw) : raw;
      return { value: value as string | null, isActive: false };
    };

    const makeMandatoryCell = (field: string, alias?: string): ErrorCell => {
      const raw = item[alias ?? field] as string | null;
      const hasError = raw?.includes('Error') || raw === '';
      return { value: hasError ? '-' : raw, isActive: false };
    };

    const propertyType = makeMandatoryCell('propertyType');
    const address = makeMandatoryCell('address', 'address1');
    const city = makeMandatoryCell('city');
    const state = makeMandatoryCell('state');
    const zipCode = makeMandatoryCell('zipCode');
    const unitNumber = makeCell('unitNumber');
    const propertyId = makeCell('propertyId');
    const unitId = makeCell('unitId');
    const bedrooms = makeCell('bedrooms');
    const bathrooms = makeCell('bathrooms');
    const squareFeet = makeCell('squareFeet');
    const propertyStatus = makeCell('propertyStatus');
    const portfolioName = makeCell('portfolioName');
    const rentReady = makeCell('rentReady', {
      transform: (v) => convertBooleanValue(v as boolean | null),
    });
    const listingDate = makeCell('listingDate');
    const isListed = makeCell('isListed', {
      transform: (v) => convertBooleanValue(v as boolean | null),
    });
    const daysOnMarket = makeCell('daysOnMarket');
    const availableDate = makeCell('availableDate');
    const leaseSignedDate = makeCell('leaseSignedDate');
    const leaseStartDate = makeCell('leaseStartDate');
    const leaseEndDate = makeCell('leaseEndDate');
    const moveInDate = makeCell('moveInDate');
    const noticeGivenDate = makeCell('noticeGivenDate');
    const moveOutDate = makeCell('moveOutDate');
    const assignee = makeCell('assignee');
    const isActive = makeCell('isActive', {
      transform: (v) => convertBooleanValue(v as boolean | null),
    });
    const propId = makeCell('propId');

    const entityRaw = extractValue(item.entity as string | null);
    const entity: ErrorCell = {
      value:
        entityRaw === '-' || entityRaw === ''
          ? getEntityFallbackValue()
          : (entityRaw as string),
      isActive: false,
    };

    const errorReasons = item.errorReasons as string[] | null;

    if (errorReasons && errorReasons.length > 0) {
      failedCount += 1;

      if (
        errorReasons.length === 1 &&
        errorReasons[0]?.includes('Failed to determine location from given address')
      ) {
        address.isActive = true;
        address.errorMessage = 'Not a valid address.';
        city.isActive = true;
        city.errorMessage = 'Not a valid city.';
        state.isActive = true;
        state.errorMessage = 'Not a valid state.';
        zipCode.isActive = true;
        zipCode.errorMessage = 'Not a valid zip code.';
      } else {
        applyPropertyErrorReasons(errorReasons, {
          address, city, state, zipCode, propertyType, propertyStatus,
          leaseSignedDate, propertyId, bedrooms, bathrooms, daysOnMarket,
          squareFeet, noticeGivenDate, moveInDate, moveOutDate, availableDate,
          rentReady, isListed, isActive, leaseStartDate, listingDate,
          assignee, leaseEndDate, entity,
        });
      }
    }

    rows.push({
      id: index + 1,
      propertyType, unitNumber, address, city, state, zipCode, propertyId,
      bedrooms, bathrooms, squareFeet, unitId, portfolioName, propertyStatus,
      rentReady, listingDate, isListed, daysOnMarket, availableDate,
      leaseSignedDate, leaseStartDate, leaseEndDate, moveInDate,
      noticeGivenDate, moveOutDate, assignee, isActive, entity, propId,
      entityUUID: item.entityUUID as string | undefined,
    });
  });

  return { rows, failedCount };
};

// ---- Error reason matching ------------------------------------------------

const ERROR_REASON_MAP: Array<{
  patterns: string[];
  cell: string;
  message: string;
}> = [
  { patterns: ['Address', 'address1'], cell: 'address', message: 'Not a valid address.' },
  { patterns: ['City', 'city'], cell: 'city', message: 'Not a valid city.' },
  { patterns: ['State', 'state'], cell: 'state', message: 'Not a valid state.' },
  { patterns: ['zip code', 'zipCode', 'Zip Code', 'zip', 'Zip'], cell: 'zipCode', message: 'Not a valid zip code.' },
  { patterns: ['property Type', 'Property Type'], cell: 'propertyType', message: 'Not a valid property type.' },
  { patterns: ['Unit Status', 'Property Status', 'property Status'], cell: 'propertyStatus', message: 'Limit 50 characters' },
  { patterns: ['Lease Signed Date', 'lease Signed Date'], cell: 'leaseSignedDate', message: 'Enter a valid date' },
  { patterns: ['Property Id', 'Property ID'], cell: 'propertyId', message: 'Limit 50 characters' },
  { patterns: ['Bedrooms'], cell: 'bedrooms', message: 'Enter a valid number' },
  { patterns: ['Bathrooms'], cell: 'bathrooms', message: 'Enter a valid number' },
  { patterns: ['Days On Market'], cell: 'daysOnMarket', message: 'Enter a valid number' },
  { patterns: ['Square Feet'], cell: 'squareFeet', message: 'Enter a valid number' },
  { patterns: ['Notice Given Date'], cell: 'noticeGivenDate', message: 'Enter a valid date' },
  { patterns: ['Move In Date'], cell: 'moveInDate', message: 'Enter a valid date' },
  { patterns: ['Move Out Date'], cell: 'moveOutDate', message: 'Enter a valid date' },
  { patterns: ['Available Date'], cell: 'availableDate', message: 'Enter a valid date' },
  { patterns: ['Rent Ready'], cell: 'rentReady', message: 'Yes or No' },
  { patterns: ['Is Listed'], cell: 'isListed', message: 'Yes or No' },
  { patterns: ['Is Active'], cell: 'isActive', message: 'Yes or No' },
  { patterns: ['Lease Start Date'], cell: 'leaseStartDate', message: 'Enter a valid date' },
  { patterns: ['Listing Date', 'listing Date'], cell: 'listingDate', message: 'Enter a valid date' },
  { patterns: ['Assignee', 'assignee'], cell: 'assignee', message: 'Enter a valid text' },
  { patterns: ['Lease End Date', 'lease End Date'], cell: 'leaseEndDate', message: 'Enter a valid date' },
  { patterns: ['Entity ID', 'Entity'], cell: 'entity', message: 'Not a valid entity.' },
];

const BOOLEAN_FIELDS = ['rentReady', 'isListed', 'isActive'];

function applyPropertyErrorReasons(
  reasons: string[],
  cells: Record<string, ErrorCell>
): void {
  for (const reason of reasons) {
    for (const mapping of ERROR_REASON_MAP) {
      if (mapping.patterns.some((p) => reason.includes(p))) {
        const target = cells[mapping.cell];
        if (target) {
          target.isActive = true;
          target.errorMessage = mapping.message;
          if (BOOLEAN_FIELDS.includes(mapping.cell)) {
            target.value = null;
          }
        }
      }
    }
  }
}

// ---- Form data builder ----------------------------------------------------

export const buildPropertyFormData = (
  data: ErrorTableRow[],
  fileId: string | null
): Record<string, unknown>[] => {
  return data.map((item) => {
    const cell = (field: string): ErrorCell | undefined =>
      item[field] as ErrorCell | undefined;

    return {
      fileId,
      propertyType: dashToNull(cell('propertyType')?.value),
      address1: dashToNull(cell('address')?.value),
      city: dashToNull(cell('city')?.value),
      state: dashToNull(cell('state')?.value),
      zipCode: dashToNull(cell('zipCode')?.value),
      unitNumber: dashToNull(cell('unitNumber')?.value),
      propId: dashToNull(cell('propId')?.value),
      propertyId: dashToNull(cell('propertyId')?.value),
      bedrooms: dashToNull(cell('bedrooms')?.value),
      bathrooms: dashToNull(cell('bathrooms')?.value),
      squareFeet: dashToNull(cell('squareFeet')?.value),
      unitId: dashToNull(cell('unitId')?.value),
      portfolioName: dashToNull(cell('portfolioName')?.value),
      propertyStatus: dashToNull(cell('propertyStatus')?.value),
      rentReady: convertYesOrNoValue(cell('rentReady')?.value ?? null),
      listingDate: dashToNull(cell('listingDate')?.value),
      isListed: convertYesOrNoValue(cell('isListed')?.value ?? null),
      daysOnMarket: dashToNull(cell('daysOnMarket')?.value),
      availableDate: dashToNull(cell('availableDate')?.value),
      leaseSignedDate: dashToNull(cell('leaseSignedDate')?.value),
      leaseStartDate: dashToNull(cell('leaseStartDate')?.value),
      leaseEndDate: dashToNull(cell('leaseEndDate')?.value),
      moveInDate: dashToNull(cell('moveInDate')?.value),
      noticeGivenDate: dashToNull(cell('noticeGivenDate')?.value),
      moveOutDate: dashToNull(cell('moveOutDate')?.value),
      assignee: dashToNull(cell('assignee')?.value),
      isActive: convertYesOrNoValue(cell('isActive')?.value ?? null),
      entity: dashToNull(cell('entity')?.value),
      entityUUID: item.entityUUID,
    };
  });
};
